import { WRITE_OFF_STATUSES, type WriteOffStatus } from '@eombor/contracts';

/**
 * NAVBAT SAHIFASINING TABLARI — faqat KO'RINISH guruhlashi.
 *
 * Holatlar to'rtta, tablar esa uchta + «Hammasi»: `REJECTED` va
 * `CANCELLED` bitta «Rad etilgan» tabiga tushadi. Ikkalasi ham YAKUNLANGAN
 * va o'tmagan akt — buxgalter uchun savol bitta: «nima o'tmadi?». Beshta
 * tab esa telefonda sig'masdi.
 *
 * Status QIYMATLARI o'zgarmaydi: API, sxema va badge hali ham to'rttasini
 * alohida biladi. Farq kartochkadagi badge'da ko'rinib turadi.
 */
export const WRITE_OFF_TABS = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as const;

export type WriteOffTab = (typeof WRITE_OFF_TABS)[number];

/**
 * Sahifa ochilganda faol tab — «Hammasi».
 *
 * Sidebar, buyruq paneli va har qanday parametrsiz havola (`/write-offs`)
 * shu tabga olib keladi; u manzilga yozilmaydi (`?status=all` qo'shilmaydi).
 *
 * ATAYLAB aniq yozilgan, `WRITE_OFF_TABS[0]` dan olinmaydi: hozir ikkalasi
 * bir xil, lekin tartib o'zgarsa default beixtiyor ko'chib ketmasligi kerak.
 */
export const DEFAULT_WRITE_OFF_TAB: WriteOffTab = 'ALL';

/**
 * Har tabga tushadigan holatlar. `ALL` — barcha to'rttasi, shuning uchun
 * «Hammasi» soni doim qolgan tablar yig'indisiga teng.
 */
export const TAB_STATUSES: Record<WriteOffTab, readonly WriteOffStatus[]> = {
  PENDING: ['PENDING'],
  APPROVED: ['APPROVED'],
  REJECTED: ['REJECTED', 'CANCELLED'],
  ALL: WRITE_OFF_STATUSES,
};

/** Akt qaysi tabda ko'rinadi (`ALL` dan tashqari). */
export const tabOfStatus = (status: WriteOffStatus): Exclude<WriteOffTab, 'ALL'> =>
  status === 'CANCELLED' ? 'REJECTED' : status;

/**
 * Tab hisoblagichlari — holatlar bo'yicha `total` lardan.
 *
 * Biror holat soni hali kelmagan bo'lsa, unga tayanadigan tab `undefined`
 * oladi: noto'g'ri son ko'rsatgandan ko'ra son ko'rsatmagan yaxshi.
 */
export const tabCounts = (
  totals: Partial<Record<WriteOffStatus, number>>,
): Record<WriteOffTab, number | undefined> => {
  const sum = (statuses: readonly WriteOffStatus[]): number | undefined => {
    let acc = 0;
    for (const status of statuses) {
      const value = totals[status];
      if (value === undefined) return undefined;
      acc += value;
    }
    return acc;
  };

  return {
    PENDING: sum(TAB_STATUSES.PENDING),
    APPROVED: sum(TAB_STATUSES.APPROVED),
    REJECTED: sum(TAB_STATUSES.REJECTED),
    ALL: sum(TAB_STATUSES.ALL),
  };
};

/* ------------------------------------------------------------------ */
/* Manzil: `/write-offs?status=rejected`                               */
/* ------------------------------------------------------------------ */

export interface WriteOffsSearch {
  status?: Lowercase<WriteOffTab>;
}

/**
 * Manzildagi qiymatni tabga aylantiradi.
 *
 * Kichik-katta harf farq qilmaydi (`?status=REJECTED` ham ishlaydi) va
 * `cancelled` — eski «Bekor qilingan» tabi — «Rad etilgan» ga olib
 * boradi. Tanilmagan qiymat jimgina sukutdagi tabga tushadi: manzilni
 * odam ham yozadi va sahifa undan yiqilmasligi kerak.
 */
export const tabFromSearchValue = (raw: unknown): WriteOffTab => {
  if (typeof raw !== 'string') return DEFAULT_WRITE_OFF_TAB;
  const value = raw.trim().toUpperCase();
  if (value === 'CANCELLED' || value === 'CANCELED') return 'REJECTED';
  return (WRITE_OFF_TABS as readonly string[]).includes(value)
    ? (value as WriteOffTab)
    : DEFAULT_WRITE_OFF_TAB;
};

/** Sukutdagi tab manzilga yozilmaydi — `/write-offs` toza qoladi. */
export const searchOfTab = (tab: WriteOffTab): WriteOffsSearch =>
  tab === DEFAULT_WRITE_OFF_TAB ? {} : { status: tab.toLowerCase() as Lowercase<WriteOffTab> };

/**
 * Manzildan tab — `status`, bo'lmasa ESKI `tab` kaliti.
 *
 * `?tab=pending` ko'rinishidagi havolalar tarqalgan: ilgari sukutdagi tab
 * «Kutilmoqda» bo'lgani uchun ular (kalit o'qilmasa ham) kutilganini
 * ochardi. Sukut «Hammasi» ga o'tgach, ular jimgina boshqa tabga tushib
 * qolmasligi uchun `tab` ham o'qiladi va `?status=` ga aylantiriladi.
 * Ikkalasi berilsa `status` ustun — u hozirgi kalit.
 */
export const tabFromSearch = (search: Record<string, unknown>): WriteOffTab =>
  tabFromSearchValue(search['status'] ?? search['tab']);

export const parseWriteOffsSearch = (search: Record<string, unknown>): WriteOffsSearch =>
  searchOfTab(tabFromSearch(search));
